/**
 * Get Request Info from Cookies
 *
 * Reads user preferences and auth state from cookies injected by native app.
 * This is the webview equivalent of the server-side getRequestInfo.
 */

import Cookies from 'js-cookie';

import {
  COLOR_SCHEME_COOKIE_NAME,
  DEFAULT_COLOR_SCHEME,
  DEFAULT_THEME,
  parseColorScheme,
  parseTheme,
  THEME_COOKIE_NAME,
} from '@components/theme/schema';
import { COOKIE_LOCALE, COOKIE_TOKEN } from '@shared-web/constants/cookies';
import {
  DEFAULT_LANGUAGE,
  isValidLanguage,
  type SupportedLanguage,
} from '@shared-web/i18n';
import type { RequestInfo } from '@shared-web/request-info';

/**
 * Parse locale from cookie value
 * Returns default language if the locale is not supported
 */
function parseLocale(value: string | undefined): SupportedLanguage {
  if (!value) {
    return DEFAULT_LANGUAGE;
  }
  if (isValidLanguage(value)) {
    return value;
  }
  return DEFAULT_LANGUAGE;
}

/**
 * Get request info from cookies
 *
 * In webview, the native app injects cookies before loading the page:
 * - token: Auth token (presence indicates logged-in state)
 * - crypto.locale: User's locale preference
 * - theme: User's theme preference (light/dark/tron)
 * - color-scheme: User's color scheme preference (default/colorblind)
 *
 * @returns RequestInfo object with all user preferences and auth state
 */
export function getRequestInfo(): RequestInfo {
  // Read cookies
  const token = Cookies.get(COOKIE_TOKEN);
  const localeCookie = Cookies.get(COOKIE_LOCALE);
  const themeCookie = Cookies.get(THEME_COOKIE_NAME);
  const colorSchemeCookie = Cookies.get(COLOR_SCHEME_COOKIE_NAME);

  // Parse values with fallbacks
  const isLoggedIn = !!token;
  const locale = parseLocale(localeCookie);
  const theme = themeCookie ? parseTheme(themeCookie) : DEFAULT_THEME;
  const colorScheme = colorSchemeCookie
    ? parseColorScheme(colorSchemeCookie)
    : DEFAULT_COLOR_SCHEME;

  return {
    hints: {
      // Use system preference as hint, theme cookie overrides this
      colorScheme: theme === 'light' ? 'light' : 'dark',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    },
    userPrefs: {
      theme,
      colorScheme,
      locale,
    },
    auth: {
      isLoggedIn,
    },
    origin: typeof window !== 'undefined' ? window.location.origin : '',
    path: typeof window !== 'undefined' ? window.location.pathname : '/',
  };
}
